/**
 * Tracking-number field for the ASN detail modal — read-only by default, with
 * inline editing for the people who raised the shipment.
 *
 * A consignment number is routinely wrong or missing at the moment an ASN is
 * created (own transport swapped for a courier after the fact, a digit dropped
 * off a hand-written note), and until it is right the customer has no way to
 * follow the delivery. Saving here re-registers the shipment with the courier
 * tracking provider, so a corrected number starts reporting immediately rather
 * than at the next sweep.
 *
 * Visibility is driven by the API's own `can_edit` flag rather than a
 * client-side role guess, so the control appears for exactly the users the
 * backend will accept a write from.
 */
import { useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { purchasingAPI } from '../../../lib/api';
import { ASNData } from '../asnModalTypes';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { toast } from 'sonner';

interface ASNTrackingNumberFieldProps {
  /** The ASN being viewed. */
  asn: ASNData;
  /** Called after a successful save so the modal can refetch courier state. */
  onUpdated: () => void;
}

/**
 * Renders the Tracking Number cell of the ASN summary grid.
 *
 * @param asn - The ASN being viewed; `can_edit` decides whether editing is offered.
 * @param onUpdated - Fired after a successful save so the caller can refetch.
 * @returns The tracking-number cell, editable in place when permitted.
 */
export default function ASNTrackingNumberField({ asn, onUpdated }: ASNTrackingNumberFieldProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(asn.tracking_number || '');
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    setValue(asn.tracking_number || '');
    setEditing(true);
  };

  const handleSave = async () => {
    const next = value.trim();
    if (next === (asn.tracking_number || '').trim()) {
      setEditing(false);
      return;
    }
    try {
      setSaving(true);
      await purchasingAPI.updateASN(asn.id, { tracking_number: next });
      onUpdated();
      setEditing(false);
      toast.success(next ? 'Tracking number updated' : 'Tracking number cleared');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to update tracking number'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <span className="text-muted-foreground">Tracking Number</span>
        <p className="font-medium font-mono flex items-center gap-1">
          {asn.tracking_number || '-'}
          {asn.can_edit && (
            <button
              type="button"
              aria-label="Edit tracking number"
              title="Edit tracking number"
              onClick={startEditing}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <span className="text-muted-foreground">Tracking Number</span>
      <div className="flex items-center gap-1 mt-0.5">
        <Input
          value={value}
          autoFocus
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="Consignment number"
          aria-label="Tracking number"
          className="h-7 font-mono text-sm"
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          aria-label="Save tracking number"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          aria-label="Cancel editing tracking number"
          disabled={saving}
          onClick={() => setEditing(false)}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
